import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { patchRequest } from "@react/helpers/fetch"
import { fmt } from "../helpers/format"
import { QLINE_TYPE_META, TypeChip, FilterPill } from "../helpers/costCategories"
import { formatMoney } from "../helpers/poTotals"
import { usePanorama } from "../context/PanoramaContext"

const GRID = "grid grid-cols-[minmax(200px,2fr)_120px_50px_44px_96px_120px_110px_110px_36px] gap-3"
const INDENT = 22

const tp = (key, opts) => I18n.t(`projects.budgets.procurement.${key}`, opts)

const PROCUREMENT_TONE = {
  frozen:            "bg-slate-100 text-slate-600 ring-slate-200",
  ordered_elsewhere: "bg-indigo-50 text-indigo-700 ring-indigo-200",
}

const ProcurementMenu = ({ row, updateUrl, onSaved }) => {
  const [open, setOpen]     = useState(false)
  const [status, setStatus] = useState(row.procurement_status || "open")
  const [note, setNote]     = useState(row.procurement_note || "")
  const [saving, setSaving] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = event => { if (!wrapRef.current?.contains(event.target)) setOpen(false) }
    document.addEventListener("click", onDocClick, true)
    return () => document.removeEventListener("click", onDocClick, true)
  }, [open])

  const apply = () => {
    setSaving(true)
    Promise.all((row.breakdown_ids || []).map(id => patchRequest({
      url:  updateUrl.replace(":id", id),
      body: { qline_breakdown: { procurement_status: status, procurement_note: status === "open" ? "" : note } },
    })))
      .then(() => { setOpen(false); onSaved && onSaved() })
      .finally(() => setSaving(false))
  }

  const options = ["open", "frozen", "ordered_elsewhere"]

  return (
    <div className="relative flex justify-end" ref={wrapRef}>
      <button
        type="button"
        title={tp("menu_title")}
        onClick={() => setOpen(value => !value)}
        className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100"
      >
        <FontAwesomeIcon icon="ellipsis-vertical" className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-20 w-72 bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-left">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{tp("menu_title")}</div>
          <div className="space-y-1">
            {options.map(option => (
              <label key={option} className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-[12.5px] ${status === option ? "bg-slate-50 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                <input type="radio" name={`procurement-${row.id}`} className="h-3.5 w-3.5" checked={status === option} onChange={() => setStatus(option)} />
                {option === "open" && row.procurement_status !== "open" ? tp("reopen") : tp(option)}
              </label>
            ))}
          </div>
          {status !== "open" && (
            <input
              className="mt-2 w-full h-8 px-2.5 rounded-md border border-slate-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-200"
              placeholder={tp("note_prompt")}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" className="px-2.5 py-1 rounded-md text-[12px] text-slate-500 hover:bg-slate-100" onClick={() => setOpen(false)}>
              {I18n.t("projects.engagements.form.cancel")}
            </button>
            <button type="button" disabled={saving} className="px-3 py-1 rounded-md text-[12px] font-medium text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-50" onClick={apply}>
              {saving ? "…" : I18n.t("projects.engagements.form.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const ProcurementChip = ({ row }) => {
  if (!row.procurement_status || row.procurement_status === "open") return null
  return (
    <span
      title={row.procurement_note || tp("frozen_hint")}
      className={`inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[10.5px] font-medium ring-1 ring-inset ${PROCUREMENT_TONE[row.procurement_status]}`}
    >
      <FontAwesomeIcon icon="snowflake" className="w-2.5 h-2.5" />
      {tp(row.procurement_status)}
      {row.procurement_note && <span className="text-slate-400 font-normal truncate max-w-[160px]">· {row.procurement_note}</span>}
    </span>
  )
}

const engagedTone = (row) => {
  const engaged = row.engaged?.cents || 0
  if (engaged === 0) return "text-slate-400"
  const costBudget = row.purchase_subtotal?.cents || 0
  if (costBudget === 0) return "text-slate-600"
  return engaged > costBudget ? "text-rose-600" : "text-emerald-600"
}

const ProgressCell = ({ row }) => {
  const quoted  = row.quantity || 0
  const ordered = row.ordered_quantity || 0
  if (ordered <= 0) return <span className="text-slate-300">—</span>
  const done    = quoted > 0 && ordered >= quoted
  const percent = quoted > 0 ? Math.min(100, Math.round((ordered / quoted) * 100)) : 100
  return (
    <div className="min-w-0" title={`${ordered} / ${quoted}`}>
      <div className={`text-[11px] tabular-nums mb-1 ${done ? "text-emerald-600 font-medium" : "text-amber-600"}`}>
        {done ? (
          <><FontAwesomeIcon icon="check" className="w-2.5 h-2.5 mr-1" />{I18n.t("projects.budgets.progress.ordered")}</>
        ) : `${percent} %`}
      </div>
      <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

const VarianceCell = ({ row, currency }) => {
  const engaged    = row.engaged?.cents || 0
  const costBudget = row.purchase_subtotal?.cents || 0
  if (engaged === 0 && (row.ordered_quantity || 0) === 0) return <span className="text-slate-300">—</span>
  const delta = costBudget - engaged
  if (costBudget === 0) return <span className="tabular-nums text-slate-400">{formatMoney(delta / 100, currency)}</span>
  return (
    <span className={`tabular-nums font-medium ${delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>
      {delta > 0 ? "+" : ""}{formatMoney(delta / 100, currency)}
    </span>
  )
}

const StatCard = ({ label, value, icon, tone, engaged, reference }) => {
  const engagedCents = engaged?.cents || 0
  const refCents     = reference?.cents || 0
  const percent      = refCents > 0 ? Math.min(100, Math.round((engagedCents / refCents) * 100)) : 0
  const over         = refCents > 0 && engagedCents > refCents
  return (
    <div className="px-4 py-3 bg-white border border-slate-200 rounded-xl">
      <div className="flex items-center gap-3">
        <span className={`w-9 h-9 grid place-items-center rounded-full ${tone}`}>
          <FontAwesomeIcon icon={icon} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{label}</div>
          <div className="text-[15px] font-semibold tabular-nums text-slate-900 truncate">{value}</div>
        </div>
      </div>
      {engagedCents > 0 && (
        <div className="mt-2.5">
          <div className="flex items-center justify-between text-[10.5px] mb-1">
            <span className="uppercase tracking-wider font-medium text-slate-400">{I18n.t("projects.budgets.engaged_label")}</span>
            <span className={`tabular-nums font-medium ${over ? "text-rose-600" : "text-slate-700"}`}>{engaged.formatted}</span>
          </div>
          {refCents > 0 && (
            <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${over ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${percent}%` }} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ComposedRow = ({ row, collapsed, onToggle }) => (
  <div className={`${GRID} px-4 py-2 items-center bg-slate-50/60 text-[12.5px]`} title={I18n.t("projects.budgets.composed_hint")}>
    <div className="min-w-0 flex items-center gap-1.5" style={{ paddingLeft: row.depth * INDENT }}>
      <button
        type="button"
        onClick={onToggle}
        title={I18n.t("quotes.builder.aggregate.toggle_collapse")}
        className="w-4 h-4 shrink-0 grid place-items-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"
      >
        <FontAwesomeIcon icon={collapsed ? "chevron-right" : "chevron-down"} className="w-2.5 h-2.5" />
      </button>
      <span className="text-slate-900 font-medium truncate" title={row.description}>{row.description || "—"}</span>
    </div>
    <div />
    <div className="text-right tabular-nums text-slate-500">{row.quantity}</div>
    <div className="text-slate-400 truncate">{row.unit || "—"}</div>
    <div />
    <div className="text-right tabular-nums text-slate-500">{fmt(row.purchase_subtotal)}</div>
    <div className="text-right tabular-nums text-slate-500">
      {(row.engaged?.cents || 0) > 0 ? fmt(row.engaged) : <span className="text-slate-300">—</span>}
    </div>
    <div />
    <div />
  </div>
)

const BudgetsSection = ({ components }) => {
  const { refresh }    = usePanorama()
  const rows           = components?.rows            || []
  const totals         = components?.totals          || {}
  const engagedTotals  = components?.engaged_totals  || {}
  const frozenTotals   = components?.frozen_totals   || {}
  const updateUrl      = components?.update_url
  const currency       = components?.currency        || "EUR"
  const [activeType, setActiveType] = useState("all")
  const [query,      setQuery]      = useState("")
  const [collapsed,  setCollapsed]  = useState(() => new Set())

  const leaves = useMemo(() => rows.filter(r => !r.composed), [rows])

  const ancestry = useMemo(() => {
    const byId = new Map(rows.map(r => [r.id, r]))
    const chainOf = r => {
      const chain = []
      for (let parent = byId.get(r.parent_id); parent; parent = byId.get(parent.parent_id)) chain.push(parent)
      return chain
    }
    return new Map(rows.map(r => [r.id, chainOf(r)]))
  }, [rows])

  const counts = useMemo(() => {
    const c = { all: leaves.length }
    leaves.forEach(r => { c[r.qline_type] = (c[r.qline_type] || 0) + 1 })
    return c
  }, [leaves])

  const filtered = useMemo(() => {
    let list = activeType === "all" ? leaves : leaves.filter(r => r.qline_type === activeType)
    if (query) {
      const q = query.toLowerCase()
      const matches = r =>
        r.description?.toLowerCase().includes(q) ||
        r.product?.name?.toLowerCase().includes(q)
      list = list.filter(r => matches(r) || ancestry.get(r.id).some(matches))
    }
    return list
  }, [leaves, ancestry, activeType, query])

  const visibleRows = useMemo(() => {
    const shown = new Set()
    filtered.forEach(r => {
      shown.add(r.id)
      ancestry.get(r.id).forEach(parent => shown.add(parent.id))
    })
    return rows.filter(r => shown.has(r.id) && !ancestry.get(r.id).some(parent => collapsed.has(parent.id)))
  }, [rows, filtered, ancestry, collapsed])

  const toggle = id => setCollapsed(current => {
    const next = new Set(current)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const filteredTotal = useMemo(
    () => filtered.reduce((acc, r) => acc + (r.purchase_subtotal?.cents || 0), 0),
    [filtered]
  )
  const filteredFmt = formatMoney(filteredTotal / 100, currency)

  const footerTotals = useMemo(() => (
    filtered.reduce((acc, row) => {
      const engagedCents = row.engaged?.cents || 0
      const costCents    = row.purchase_subtotal?.cents || 0
      acc.cost    += costCents
      acc.engaged += engagedCents
      if (engagedCents > 0 || (row.ordered_quantity || 0) > 0) {
        acc.variance   += costCents - engagedCents
        acc.hasEngaged  = true
      }
      return acc
    }, { cost: 0, engaged: 0, variance: 0, hasEngaged: false })
  ), [filtered])

  return (
    <section id="section-budgets" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.budgets.title")}</h2>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          {I18n.t("projects.budgets.subtitle")}
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
        <StatCard label={I18n.t("projects.budgets.total_budget")}            value={fmt(totals.total)}          icon="wallet"              tone="bg-slate-100 text-slate-700"   engaged={engagedTotals.total}          reference={totals.total} />
        <StatCard label={I18n.t("quotes.new.cost_category.labor")}           value={fmt(totals.labor)}          icon="user-clock"          tone="bg-blue-50 text-blue-700"      engaged={engagedTotals.labor}          reference={totals.labor} />
        <StatCard label={I18n.t("quotes.new.cost_category.equipment")}       value={fmt(totals.equipment)}      icon="screwdriver-wrench"  tone="bg-violet-50 text-violet-700"  engaged={engagedTotals.equipment}      reference={totals.equipment} />
        <StatCard label={I18n.t("quotes.new.cost_category.materials")}       value={fmt(totals.materials)}      icon="boxes-stacked"       tone="bg-emerald-50 text-emerald-700" engaged={engagedTotals.materials}     reference={totals.materials} />
        <StatCard label={I18n.t("quotes.new.cost_category.subcontracting")}  value={fmt(totals.subcontracting)} icon="handshake"           tone="bg-amber-50 text-amber-700"    engaged={engagedTotals.subcontracting} reference={totals.subcontracting} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <FilterPill active={activeType === "all"} onClick={() => setActiveType("all")} label={I18n.t("projects.budgets.filter_all")} count={counts.all} />
            {Object.keys(QLINE_TYPE_META).filter(k => k !== "other").map(type => (
              counts[type] ? (
                <FilterPill
                  key={type}
                  active={activeType === type}
                  onClick={() => setActiveType(type)}
                  label={QLINE_TYPE_META[type].label}
                  count={counts[type]}
                />
              ) : null
            ))}
          </div>
          <span className="flex-1" />
          <div className="relative">
            <FontAwesomeIcon icon="magnifying-glass" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={I18n.t("projects.budgets.search_placeholder")}
              className="pl-7 pr-3 py-1 bg-slate-50 rounded-md text-[12.5px] outline-none focus:ring-2 focus:ring-blue-200 w-56"
            />
          </div>
          <span className="text-[12.5px] text-slate-500 tabular-nums">
            {I18n.t("projects.budgets.line_count", { count: filtered.length, amount: filteredFmt })}
          </span>
        </div>

        <div className={`${GRID} px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
          <div>{I18n.t("projects.budgets.table.description")}</div>
          <div>{I18n.t("projects.budgets.table.category")}</div>
          <div className="text-right">{I18n.t("projects.budgets.table.qty")}</div>
          <div>{I18n.t("projects.budgets.table.unit")}</div>
          <div>{I18n.t("projects.budgets.table.progress")}</div>
          <div className="text-right">{I18n.t("projects.budgets.table.purchase_subtotal")}</div>
          <div className="text-right">{I18n.t("projects.budgets.table.engaged")}</div>
          <div className="text-right">{I18n.t("projects.budgets.table.variance")}</div>
          <div />
        </div>

        <div className="divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-[13px] text-slate-500">
              {I18n.t("projects.budgets.empty")}
            </div>
          ) : visibleRows.map(row => row.composed ? (
            <ComposedRow key={row.id} row={row} collapsed={collapsed.has(row.id)} onToggle={() => toggle(row.id)} />
          ) : (
            <div
              key={row.id}
              className={`${GRID} px-4 py-2.5 items-center hover:bg-slate-50/60 text-[12.5px] ${row.procurement_status && row.procurement_status !== "open" ? "opacity-75" : ""}`}
            >
              <div className="min-w-0" style={{ paddingLeft: row.depth * INDENT }}>
                <div className="text-slate-900 truncate" title={row.description}>{row.description || "—"}</div>
                {row.product?.name && (
                  <div className="text-[11px] text-slate-500 truncate">{row.product.name}</div>
                )}
                <ProcurementChip row={row} />
              </div>
              <div><TypeChip type={row.qline_type} /></div>
              <div className="text-right tabular-nums text-slate-700">{row.quantity}</div>
              <div className="text-slate-500 truncate">{row.unit || "—"}</div>
              <div><ProgressCell row={row} /></div>
              <div className="text-right tabular-nums text-slate-900 font-medium">{fmt(row.purchase_subtotal)}</div>
              <div className={`text-right tabular-nums font-medium ${engagedTone(row)}`}>
                {(row.engaged?.cents || 0) > 0 || (row.ordered_quantity || 0) > 0
                  ? fmt(row.engaged)
                  : <span className="text-slate-300">—</span>}
              </div>
              <div className="text-right"><VarianceCell row={row} currency={currency} /></div>
              <div>
                {row.can_freeze && updateUrl && <ProcurementMenu row={row} updateUrl={updateUrl} onSaved={refresh} />}
              </div>
            </div>
          ))}
        </div>

        {filtered.length > 0 && (
          <div className={`${GRID} px-4 py-2.5 items-center bg-slate-50/70 border-t border-slate-200 rounded-b-xl text-[12.5px] font-semibold`}>
            <div className="text-slate-700">
              {I18n.t("projects.budgets.footer_total")}
              {(frozenTotals.total?.cents || 0) > 0 && (
                <span className="block text-[11px] font-normal text-slate-500">{tp("frozen_total", { amount: frozenTotals.total.formatted })}</span>
              )}
            </div>
            <div />
            <div />
            <div />
            <div />
            <div className="text-right tabular-nums text-slate-900">{formatMoney(footerTotals.cost / 100, currency)}</div>
            <div className={`text-right tabular-nums ${footerTotals.cost > 0 && footerTotals.engaged > footerTotals.cost ? "text-rose-600" : "text-slate-900"}`}>
              {footerTotals.hasEngaged ? formatMoney(footerTotals.engaged / 100, currency) : <span className="text-slate-300 font-normal">—</span>}
            </div>
            <div className={`text-right tabular-nums ${footerTotals.variance < 0 ? "text-rose-600" : "text-emerald-600"}`}>
              {footerTotals.hasEngaged
                ? `${footerTotals.variance > 0 ? "+" : ""}${formatMoney(footerTotals.variance / 100, currency)}`
                : <span className="text-slate-300 font-normal">—</span>}
            </div>
            <div />
          </div>
        )}
      </div>
    </section>
  )
}

export default BudgetsSection
